/**
 * Bills: a supplier's or subcontractor's invoice to us, transposed into JARVIS.
 *
 * CIS (HMRC CIS340): the deduction is taken from the labour element only,
 * excluding VAT and the cost of materials. VAT is charged on the full net
 * amount, before any CIS deduction. Under the domestic reverse charge the
 * subcontractor charges no VAT and we account for it ourselves.
 */
import { CIS_BAND_RATES, type BillPaymentTerms, type CisBand } from './accounts.model';
import { chargesVat, money, normaliseVatTreatment, type VatTreatment } from './arrangements.model';

export type BillLineKind = 'labour' | 'materials' | 'other';

export const BILL_LINE_KIND_LABELS: Record<BillLineKind, string> = {
  labour: 'Labour',
  materials: 'Materials',
  other: 'Other',
};

/** FreeAgent accepts at most 40 items on a bill. */
export const MAX_BILL_LINES = 40;
/** FreeAgent's attachment limit. */
export const MAX_BILL_ATTACHMENT_BYTES = 5 * 1024 * 1024;

export type BillLine = {
  description: string;
  kind: BillLineKind;
  /** Ex-VAT amount. The source of truth; quantity and unit price are optional detail. */
  net: number;
  quantity?: number;
  unitPrice?: number;
};

/** `archived`: copied from the old subcontractor invoices. Read-only and never sent to FreeAgent. */
export type BillStatus = 'draft' | 'sent' | 'error' | 'archived';

export const BILL_STATUS_LABELS: Record<BillStatus, string> = {
  draft: 'Draft',
  sent: 'In FreeAgent',
  error: 'Needs attention',
  archived: 'Archived',
};

export type BillTotals = {
  labourNet: number;
  materialsNet: number;
  otherNet: number;
  netTotal: number;
  /** VAT the supplier charges us. Zero under reverse charge, exempt or outside scope. */
  vatCharged: number;
  /** VAT we account for ourselves under the reverse charge. Not paid to the supplier. */
  reverseChargeVat: number;
  grossTotal: number;
  cisRate: number | null;
  cisDeduction: number;
  /** What we pay the supplier: gross less the CIS deduction. */
  payableTotal: number;
};

export type AccountBill = {
  id: string;
  accountId: string;
  reference: string;
  datedOn: string;
  dueOn: string | null;
  description: string | null;
  lines: BillLine[];
  vatTreatment: VatTreatment;
  vatPct: number;
  cisBand: CisBand;
  totals: BillTotals;
  attachmentPath: string | null;
  attachmentName: string | null;
  attachmentContentType: string | null;
  attachmentSize: number | null;
  labourCategoryUrl: string | null;
  materialsCategoryUrl: string | null;
  otherCategoryUrl: string | null;
  status: BillStatus;
  freeagentBillUrl: string | null;
  syncError: string | null;
  lastSyncedAt: string | null;
  /** Edited since it was last sent; FreeAgent needs updating. */
  freeagentOutOfDate: boolean;
  /** The CIS deduction FreeAgent calculated, to check against ours. */
  freeagentCisDeduction: number | null;
  paidOn: string | null;
  /** Which Keystone company the bill is addressed to. */
  billToCompanyId: string | null;
  notes: string | null;
  createdAt: string;
  /** Copied from the old subcontractor invoices; totals are as originally recorded. */
  isLegacy: boolean;
  legacySnapshot: Record<string, any> | null;
};

export type AccountBillInput = {
  accountId: string;
  reference: string;
  datedOn: string;
  dueOn?: string | null;
  description?: string | null;
  lines: BillLine[];
  vatTreatment: VatTreatment;
  vatPct?: number;
  cisBand: CisBand;
  billToCompanyId?: string | null;
  notes?: string | null;
};

const LINE_KINDS = new Set<BillLineKind>(['labour', 'materials', 'other']);
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function optionalNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function normaliseLine(raw: any): BillLine {
  const line: BillLine = {
    description: String(raw?.description || '').trim(),
    kind: LINE_KINDS.has(raw?.kind) ? raw.kind : 'other',
    net: money(Number(raw?.net) || 0),
  };
  const quantity = optionalNumber(raw?.quantity);
  const unitPrice = optionalNumber(raw?.unitPrice);
  if (quantity !== undefined) line.quantity = quantity;
  if (unitPrice !== undefined) line.unitPrice = unitPrice;
  return line;
}

/**
 * Due date from the account's payment terms. Weeks run Monday to Sunday: 'weekly_same_friday'
 * is the Friday of the invoice's week (never before the invoice date), 'weekly_next_friday'
 * the Friday after that. Null when the account has no terms.
 */
export function billDueDate(datedOn: string, terms: BillPaymentTerms): string | null {
  if (terms === 'none' || !DATE_PATTERN.test(datedOn)) return null;
  const [year, month, day] = datedOn.split('-').map(Number);
  const dated = Date.UTC(year, month - 1, day);
  const mondayBased = (new Date(dated).getUTCDay() + 6) % 7;
  const DAY = 86400000;
  const friday = dated + (4 - mondayBased) * DAY;
  const due = terms === 'weekly_next_friday' ? friday + 7 * DAY : Math.max(friday, dated);
  return new Date(due).toISOString().slice(0, 10);
}

/** Net for a line priced as quantity × unit price. */
export function lineNetFrom(quantity: number, unitPrice: number): number {
  return money((Number(quantity) || 0) * (Number(unitPrice) || 0));
}

export function computeBillTotals(args: {
  lines: BillLine[];
  vatTreatment: VatTreatment;
  vatPct: number;
  cisBand: CisBand;
}): BillTotals {
  const sumKind = (kind: BillLineKind) => money(
    args.lines.filter((line) => line.kind === kind).reduce((total, line) => total + (Number(line.net) || 0), 0)
  );
  const labourNet = sumKind('labour');
  const materialsNet = sumKind('materials');
  const otherNet = sumKind('other');
  const netTotal = money(labourNet + materialsNet + otherNet);
  const vatCharged = chargesVat(args.vatTreatment) ? money(netTotal * args.vatPct) : 0;
  const reverseChargeVat = args.vatTreatment === 'reverse_charge' ? money(netTotal * args.vatPct) : 0;
  const grossTotal = money(netTotal + vatCharged);
  const cisRate = CIS_BAND_RATES[args.cisBand];
  const cisDeduction = cisRate ? money(labourNet * cisRate) : 0;
  return {
    labourNet,
    materialsNet,
    otherNet,
    netTotal,
    vatCharged,
    reverseChargeVat,
    grossTotal,
    cisRate,
    cisDeduction,
    payableTotal: money(grossTotal - cisDeduction),
  };
}

/** Returns the first problem with the input, or null when it can be saved. */
export function validateBillInput(input: AccountBillInput): string | null {
  if (!String(input.accountId || '').trim()) return 'Choose who the bill is from.';
  if (!String(input.reference || '').trim()) return 'Enter the supplier’s invoice number.';
  if (!DATE_PATTERN.test(String(input.datedOn || ''))) return 'Enter the invoice date.';
  if (input.dueOn) {
    if (!DATE_PATTERN.test(input.dueOn)) return 'The due date does not look right.';
    if (input.dueOn < input.datedOn) return 'The due date is before the invoice date.';
  }
  const lines = input.lines || [];
  if (lines.length === 0) return 'Add at least one line.';
  if (lines.length > MAX_BILL_LINES) return `FreeAgent accepts up to ${MAX_BILL_LINES} lines on a bill.`;
  for (const [index, line] of lines.entries()) {
    if (!String(line.description || '').trim()) return `Line ${index + 1} needs a description.`;
    if (!(Number(line.net) > 0)) return `Line ${index + 1} needs an amount greater than zero.`;
  }
  const vatPct = input.vatPct ?? 0.2;
  if (!(vatPct >= 0 && vatPct < 1)) return 'The VAT rate must be between 0% and 100%.';
  if (input.cisBand !== 'none' && !lines.some((line) => line.kind === 'labour')) {
    return 'This account is a CIS subcontractor, so the bill needs at least one labour line.';
  }
  return null;
}

export function toDbBill(input: AccountBillInput): Record<string, unknown> {
  const lines = (input.lines || []).map(normaliseLine);
  const vatTreatment = normaliseVatTreatment(input.vatTreatment);
  const vatPct = input.vatPct ?? 0.2;
  const totals = computeBillTotals({ lines, vatTreatment, vatPct, cisBand: input.cisBand });
  return {
    account_id: input.accountId,
    reference: input.reference.trim(),
    dated_on: input.datedOn,
    due_on: input.dueOn || null,
    description: String(input.description || '').trim() || null,
    lines,
    vat_treatment: vatTreatment,
    vat_pct: vatPct,
    cis_band: input.cisBand,
    bill_to_company_id: input.billToCompanyId || null,
    labour_net: totals.labourNet,
    materials_net: totals.materialsNet,
    other_net: totals.otherNet,
    net_total: totals.netTotal,
    vat_charged: totals.vatCharged,
    gross_total: totals.grossTotal,
    cis_deduction: totals.cisDeduction,
    payable_total: totals.payableTotal,
    notes: String(input.notes || '').trim() || null,
  };
}

/**
 * Totals for a bill copied from the old subcontractor invoices, exactly as recorded there.
 * The old page charged VAT after deducting CIS, so recalculating would change history.
 */
function storedLegacyTotals(row: Record<string, any>, cisBand: CisBand): BillTotals {
  const stored = (column: string) => money(Number(row[column]) || 0);
  const legacyPercent = Number(row.legacy_snapshot?.cis_percent);
  return {
    labourNet: stored('labour_net'),
    materialsNet: stored('materials_net'),
    otherNet: stored('other_net'),
    netTotal: stored('net_total'),
    vatCharged: stored('vat_charged'),
    reverseChargeVat: 0,
    grossTotal: stored('gross_total'),
    cisRate: Number.isFinite(legacyPercent) && legacyPercent > 0 ? legacyPercent / 100 : CIS_BAND_RATES[cisBand],
    cisDeduction: stored('cis_deduction'),
    payableTotal: stored('payable_total'),
  };
}

const BILL_STATUSES = new Set<BillStatus>(['draft', 'sent', 'error', 'archived']);

export function fromDbBill(row: Record<string, any>): AccountBill {
  const lines = Array.isArray(row.lines) ? row.lines.map(normaliseLine) : [];
  const vatTreatment = normaliseVatTreatment(row.vat_treatment);
  const vatPct = row.vat_pct != null ? Number(row.vat_pct) : 0.2;
  const cisBand: CisBand = ['gross', 'standard', 'higher'].includes(row.cis_band) ? row.cis_band : 'none';
  const status: BillStatus = BILL_STATUSES.has(row.status) ? row.status : 'draft';
  return {
    id: String(row.id),
    accountId: String(row.account_id),
    reference: String(row.reference || ''),
    datedOn: String(row.dated_on || ''),
    dueOn: row.due_on ?? null,
    description: row.description ?? null,
    lines,
    vatTreatment,
    vatPct,
    cisBand,
    totals: status === 'archived'
      ? storedLegacyTotals(row, cisBand)
      : computeBillTotals({ lines, vatTreatment, vatPct, cisBand }),
    attachmentPath: row.attachment_path ?? null,
    attachmentName: row.attachment_name ?? null,
    attachmentContentType: row.attachment_content_type ?? null,
    attachmentSize: row.attachment_size != null ? Number(row.attachment_size) : null,
    labourCategoryUrl: row.labour_category_url ?? null,
    materialsCategoryUrl: row.materials_category_url ?? null,
    otherCategoryUrl: row.other_category_url ?? null,
    status,
    freeagentBillUrl: row.freeagent_bill_url ?? null,
    syncError: row.sync_error ?? null,
    lastSyncedAt: row.last_synced_at ?? null,
    freeagentOutOfDate: !!row.freeagent_out_of_date,
    freeagentCisDeduction: row.freeagent_cis_deduction != null ? Number(row.freeagent_cis_deduction) : null,
    paidOn: row.paid_on ?? null,
    billToCompanyId: row.bill_to_company_id ?? null,
    notes: row.notes ?? null,
    createdAt: String(row.created_at || ''),
    isLegacy: status === 'archived' || !!row.legacy_invoice_id,
    legacySnapshot: row.legacy_snapshot && typeof row.legacy_snapshot === 'object' ? row.legacy_snapshot : null,
  };
}

export type BillActions = {
  edit: boolean;
  delete: boolean;
  send: boolean;
  /** Push local edits to the bill already in FreeAgent. */
  updateFreeAgent: boolean;
  refresh: boolean;
  markPaid: boolean;
  unmarkPaid: boolean;
};

/**
 * Every button on the bills pages asks this. Archived bills are history and stay as recorded.
 * Bills in FreeAgent are paid there (Bill Payment on the bank transaction), so paid state comes
 * back with Refresh; bills not in FreeAgent are marked paid here.
 */
export function allowedBillActions(
  bill: Pick<AccountBill, 'status' | 'freeagentBillUrl' | 'freeagentOutOfDate' | 'paidOn'>,
): BillActions {
  if (bill.status === 'archived') {
    return { edit: false, delete: false, send: false, updateFreeAgent: false, refresh: false, markPaid: false, unmarkPaid: false };
  }
  const inFreeAgent = !!bill.freeagentBillUrl;
  const paidInFreeAgent = inFreeAgent && !!bill.paidOn;
  return {
    edit: !paidInFreeAgent,
    delete: !paidInFreeAgent,
    send: !inFreeAgent,
    updateFreeAgent: inFreeAgent && bill.freeagentOutOfDate && !paidInFreeAgent,
    refresh: inFreeAgent,
    markPaid: !inFreeAgent && !bill.paidOn,
    unmarkPaid: !inFreeAgent && !!bill.paidOn,
  };
}

export function billStatusLabel(bill: Pick<AccountBill, 'status' | 'freeagentOutOfDate' | 'paidOn'>): string {
  if (bill.status === 'sent' && bill.freeagentOutOfDate) return 'Update pending';
  if (bill.status === 'sent' && bill.paidOn) return 'Paid';
  if (bill.status === 'draft' && bill.paidOn) return 'Paid (not in FreeAgent)';
  return BILL_STATUS_LABELS[bill.status];
}

/** FreeAgent's CIS band names, as used on contacts. */
export const FREEAGENT_CIS_BANDS: Record<Exclude<CisBand, 'none'>, string> = {
  gross: 'cis_gross',
  standard: 'cis_standard',
  higher: 'cis_higher',
};

export type BillCategoryUrls = Partial<Record<BillLineKind, string | null>>;

/** FreeAgent lists PDFs as application/x-pdf. */
export function freeAgentAttachmentContentType(contentType: string | null | undefined): string | null {
  const type = String(contentType || '').trim().toLowerCase();
  if (type === 'application/pdf' || type === 'application/x-pdf') return 'application/x-pdf';
  if (['image/png', 'image/x-png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(type)) return type;
  return null;
}

/**
 * Builds the POST /v2/bills body. One bill item per line, with the category for its kind.
 * Throws when a category for a used kind has not been chosen.
 */
export function buildFreeAgentBillPayload(args: {
  bill: AccountBill;
  contactUrl: string;
  categories: BillCategoryUrls;
  attachment?: { base64: string; fileName: string; contentType: string } | null;
}): { bill: Record<string, unknown> } {
  const { bill } = args;
  // Under the domestic reverse charge FreeAgent works the VAT out itself (and books it both ways
  // on the VAT return): it only accepts 'Auto' or '0.0' as the rate, so 'Auto' it is.
  const salesTaxRate = bill.vatTreatment === 'reverse_charge'
    ? 'Auto'
    : bill.vatTreatment === 'standard'
      ? String(Math.round(bill.vatPct * 1000) / 10)
      : '0';
  const salesTaxStatus = bill.vatTreatment === 'exempt'
    ? 'EXEMPT'
    : bill.vatTreatment === 'outside_scope' ? 'OUT_OF_SCOPE' : 'TAXABLE';

  const items = bill.lines.map((line) => {
    const category = String(args.categories[line.kind] || '').trim();
    if (!category) {
      throw new Error(`Choose a FreeAgent category for ${BILL_LINE_KIND_LABELS[line.kind].toLowerCase()} lines.`);
    }
    return {
      category,
      description: line.description,
      total_value_ex_tax: line.net.toFixed(2),
      sales_tax_rate: salesTaxRate,
      sales_tax_status: salesTaxStatus,
    };
  });

  const payload: Record<string, unknown> = {
    contact: args.contactUrl,
    reference: bill.reference,
    dated_on: bill.datedOn,
    // FreeAgent requires a due date; without one the bill is due on receipt.
    due_on: bill.dueOn || bill.datedOn,
    bill_items: items,
  };
  if (bill.vatTreatment === 'reverse_charge') payload.ec_status = 'Reverse Charge';
  const comments = [bill.description, bill.notes].filter(Boolean).join('\n\n');
  if (comments) payload.comments = comments;

  if (args.attachment) {
    const contentType = freeAgentAttachmentContentType(args.attachment.contentType);
    if (!contentType) throw new Error('FreeAgent only accepts PDF, PNG, JPEG or GIF attachments.');
    payload.attachment = {
      data: args.attachment.base64,
      file_name: args.attachment.fileName,
      content_type: contentType,
      description: `Supplier invoice ${bill.reference}`,
    };
  }
  return { bill: payload };
}

/**
 * FreeAgent's CIS for Contractors categories. Only items in these count as CIS: labour (098)
 * has the deduction taken by FreeAgent from the contact's band, materials (099) are reported
 * without one. Anything else on a CIS bill would be recorded as an ordinary cost.
 * 'Other' lines carry no deduction here, so they go to 099 too; FreeAgent's travel (097) and
 * accommodation (096) categories are subject to deduction and would not match our figure.
 */
export const CIS_CATEGORY_CODES: Record<BillLineKind, string[]> = {
  labour: ['098'],
  materials: ['099'],
  other: ['099'],
};

const LINE_KIND_ORDER: BillLineKind[] = ['labour', 'materials', 'other'];

type CategoryRow = { url: string; nominal_code?: string | number | null };

export function nominalCode(category: { nominal_code?: string | number | null }): string {
  const code = String(category.nominal_code ?? '').trim();
  return /^\d+$/.test(code) ? code.padStart(3, '0') : code;
}

/** The categories a line of this kind may use. CIS bills are limited to the CIS categories. */
export function billCategoryChoices<T extends CategoryRow>(kind: BillLineKind, cisBand: CisBand, categories: T[]): T[] {
  if (cisBand === 'none') return categories;
  return categories.filter((category) => CIS_CATEGORY_CODES[kind].includes(nominalCode(category)));
}

/** First guess at a category: the CIS category for CIS labour and materials. */
export function defaultBillCategory(kind: BillLineKind, cisBand: CisBand, categories: CategoryRow[]): string | null {
  if (cisBand === 'none') return null;
  return categories.find((category) => nominalCode(category) === CIS_CATEGORY_CODES[kind][0])?.url || null;
}

/** Returns why the chosen categories would not record the bill (and its CIS) correctly, or null. */
export function billCategoryProblem(args: {
  cisBand: CisBand;
  lines: BillLine[];
  chosen: BillCategoryUrls;
  categories: CategoryRow[];
}): string | null {
  const kinds = LINE_KIND_ORDER.filter((kind) => args.lines.some((line) => line.kind === kind));
  for (const kind of kinds) {
    const label = BILL_LINE_KIND_LABELS[kind];
    const url = String(args.chosen[kind] || '').trim();
    if (!url) return `Choose a FreeAgent category for ${label.toLowerCase()} lines.`;
    if (args.cisBand === 'none') continue;
    const category = args.categories.find((row) => row.url === url);
    if (!category) return `The ${label.toLowerCase()} category is not in FreeAgent’s list. Choose it again.`;
    if (!CIS_CATEGORY_CODES[kind].includes(nominalCode(category))) {
      return `${label} lines on a CIS bill must use category ${CIS_CATEGORY_CODES[kind].join(' or ')} so FreeAgent records the CIS. `
        + 'If those categories are missing, turn on CIS for Contractors in FreeAgent.';
    }
  }
  return null;
}

function formatPounds(value: number): string {
  return `£${value.toFixed(2)}`;
}

/**
 * Compares FreeAgent's CIS deduction with ours after sending. FreeAgent takes the rate from the
 * contact when the bill is created and never changes it, so a mismatch means delete and resend.
 */
export function cisDeductionMismatch(
  bill: Pick<AccountBill, 'cisBand' | 'totals'>,
  freeAgentDeduction: number | string | null | undefined,
): string | null {
  if (bill.cisBand === 'none') return null;
  const expected = bill.totals.cisDeduction;
  const actual = freeAgentDeduction == null || freeAgentDeduction === '' ? NaN : Number(freeAgentDeduction);
  if (!Number.isFinite(actual)) {
    return 'FreeAgent did not record a CIS deduction on this bill. Check the contact is a CIS subcontractor and the lines use 098/099, then delete the bill and send it again.';
  }
  if (Math.abs(money(actual) - expected) <= 0.01) return null;
  return `FreeAgent deducted ${formatPounds(money(actual))} CIS but this bill expects ${formatPounds(expected)}. `
    + 'FreeAgent fixes the rate from the contact when a bill is created, so delete the bill and send it again.';
}

/**
 * PUT /v2/bills/:id body. FreeAgent has no bill item endpoint, so the old items are removed
 * and the current lines added. The attachment is left as it is.
 */
export function buildFreeAgentBillUpdatePayload(args: {
  bill: AccountBill;
  contactUrl: string;
  categories: BillCategoryUrls;
  existingItemUrls: string[];
}): { bill: Record<string, unknown> } {
  const { bill } = buildFreeAgentBillPayload({ bill: args.bill, contactUrl: args.contactUrl, categories: args.categories });
  const items = bill.bill_items as Record<string, unknown>[];
  return {
    bill: {
      ...bill,
      bill_items: [...args.existingItemUrls.map((url) => ({ url, _destroy: 1 })), ...items],
    },
  };
}

/** The company a bill is addressed to, as printed. */
export type BillToParty = {
  name: string;
  address?: string | null;
  vatNumber?: string | null;
  companyNumber?: string | null;
  paymentTerms?: string | null;
};

/** The name and address printed on an old subcontractor invoice. */
export function legacyBillTo(bill: Pick<AccountBill, 'legacySnapshot'>): BillToParty | null {
  const name = String(bill.legacySnapshot?.to_company_name || '').trim();
  if (!name) return null;
  return { name, address: bill.legacySnapshot?.to_address ?? null };
}

export type BillIssuer = {
  companyName: string;
  address?: string | null;
  utrNumber?: string | null;
  niNumber?: string | null;
  vatNumber?: string | null;
  logoUrl?: string | null;
  bankName?: string | null;
  bankAccountName?: string | null;
  bankSortCode?: string | null;
  bankAccountNumber?: string | null;
};

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function ukDate(iso: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/**
 * The subcontractor's invoice to us as a self-contained A4 page, for /api/export-pdf and
 * as the FreeAgent attachment when there is no scanned copy.
 */
export function buildBillHtml(args: {
  bill: AccountBill;
  issuer: BillIssuer;
  billTo: BillToParty | null;
  origin?: string;
}): string {
  const { bill, issuer, billTo } = args;
  const totals = bill.totals;
  const gbp = (value: number) => escapeHtml(formatPounds(value));
  const lineRows = (kind: BillLineKind) => {
    const rows = bill.lines.filter((line) => line.kind === kind);
    if (rows.length === 0) return '';
    return `<tr class="group"><td colspan="4">${escapeHtml(BILL_LINE_KIND_LABELS[kind])}</td></tr>`
      + rows.map((line) => `<tr><td>${escapeHtml(line.description)}</td>`
        + `<td class="num">${line.quantity != null ? escapeHtml(line.quantity) : ''}</td>`
        + `<td class="num">${line.unitPrice != null ? gbp(line.unitPrice) : ''}</td>`
        + `<td class="num">${gbp(line.net)}</td></tr>`).join('');
  };
  const totalRow = (label: string, value: string, strong = false) =>
    `<tr${strong ? ' class="strong"' : ''}><td colspan="3">${escapeHtml(label)}</td><td class="num">${value}</td></tr>`;
  const cisPercent = totals.cisRate != null ? Math.round(totals.cisRate * 1000) / 10 : null;
  const issuerIds = [
    issuer.utrNumber ? `UTR: ${issuer.utrNumber}` : null,
    issuer.niNumber ? `NI: ${issuer.niNumber}` : null,
    issuer.vatNumber ? `VAT: ${issuer.vatNumber}` : null,
  ].filter(Boolean).join(' · ');
  const bank = [
    issuer.bankName ? `Bank: ${issuer.bankName}` : null,
    issuer.bankAccountName ? `Account: ${issuer.bankAccountName}` : null,
    issuer.bankSortCode ? `Sort: ${issuer.bankSortCode}` : null,
    issuer.bankAccountNumber ? `Account No: ${issuer.bankAccountNumber}` : null,
  ].filter(Boolean).join(' · ');
  const billToIds = [
    billTo?.vatNumber ? `VAT: ${billTo.vatNumber}` : null,
    billTo?.companyNumber ? `Company No: ${billTo.companyNumber}` : null,
  ].filter(Boolean).join(' · ');

  return `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${escapeHtml(bill.reference)}</title>
${args.origin ? `<base href="${escapeHtml(args.origin)}/" />` : ''}
<style>
  @page { size: A4; margin: 18mm; }
  body { font-family: Arial, sans-serif; font-size: 12px; color: #333; margin: 0; }
  .hdr { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; }
  .muted { color: #666; font-size: 11px; }
  .parties { display: flex; gap: 24px; margin: 16px 0; }
  .parties > div { flex: 1; }
  .label { font-weight: bold; margin-bottom: 4px; }
  .pre { white-space: pre-line; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
  th { background: #f5f5f5; }
  .num { text-align: right; white-space: nowrap; }
  tr.group td { font-weight: bold; background: #fafafa; }
  tr.strong td { font-weight: bold; }
  .bank { margin-top: 16px; text-align: center; }
  .logo { max-height: 90px; max-width: 220px; object-fit: contain; }
</style></head><body>
<div class="hdr">
  <div>
    <div class="title">Invoice ${escapeHtml(bill.reference)}</div>
    <div class="muted">Date: ${escapeHtml(ukDate(bill.datedOn))}${bill.dueOn ? ` · Due: ${escapeHtml(ukDate(bill.dueOn))}` : ''}</div>
  </div>
  ${issuer.logoUrl ? `<img class="logo" src="${escapeHtml(issuer.logoUrl)}" alt="" />` : ''}
</div>
<div class="parties">
  <div>
    <div class="label">From</div>
    <div>${escapeHtml(issuer.companyName)}</div>
    ${issuer.address ? `<div class="pre">${escapeHtml(issuer.address)}</div>` : ''}
    ${issuerIds ? `<div class="muted">${escapeHtml(issuerIds)}</div>` : ''}
  </div>
  <div>
    <div class="label">Bill to</div>
    <div>${escapeHtml(billTo?.name || '')}</div>
    ${billTo?.address ? `<div class="pre">${escapeHtml(billTo.address)}</div>` : ''}
    ${billToIds ? `<div class="muted">${escapeHtml(billToIds)}</div>` : ''}
    ${billTo?.paymentTerms ? `<div class="muted">Terms: ${escapeHtml(billTo.paymentTerms)}</div>` : ''}
  </div>
</div>
${bill.description ? `<div><div class="label">Description</div><div>${escapeHtml(bill.description)}</div></div>` : ''}
<table>
  <thead><tr><th>Description</th><th class="num">Qty</th><th class="num">Unit</th><th class="num">Total</th></tr></thead>
  <tbody>${lineRows('labour')}${lineRows('materials')}${lineRows('other')}</tbody>
  <tfoot>
    ${totalRow('Labour subtotal', gbp(totals.labourNet))}
    ${totalRow('Materials subtotal', gbp(totals.materialsNet))}
    ${totals.otherNet ? totalRow('Other', gbp(totals.otherNet)) : ''}
    ${totalRow('Subtotal', gbp(totals.netTotal))}
    ${totals.vatCharged ? totalRow(`VAT${bill.vatPct ? ` (${Math.round(bill.vatPct * 1000) / 10}%)` : ''}`, gbp(totals.vatCharged)) : ''}
    ${bill.vatTreatment === 'reverse_charge' ? totalRow('Reverse charge: customer to account to HMRC for the VAT', gbp(totals.reverseChargeVat)) : ''}
    ${totals.cisDeduction ? totalRow(`CIS deduction${cisPercent != null ? ` (${cisPercent}% of labour)` : ''}`, `-${gbp(totals.cisDeduction)}`) : ''}
    ${totalRow('Total payable', gbp(totals.payableTotal), true)}
  </tfoot>
</table>
${bill.notes ? `<p class="muted pre">${escapeHtml(bill.notes)}</p>` : ''}
${bank ? `<div class="bank">${escapeHtml(bank)}</div>` : ''}
</body></html>`;
}
